// /src/components/security/UsersByRoleModal.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Modal,
  Button,
  Table,
  Badge,
  InputGroup,
  FormControl,
  OverlayTrigger,
  Tooltip,
  Form,
} from "react-bootstrap";
import {
  Users,
  Search,
  Copy,
  CheckCircle2,
  XCircle,
  Mail,
  ChevronUp,
  ChevronDown,
  Filter,
} from "lucide-react";

/**
 * UsersByRoleModal
 * Modal estilizado para listar usuarios vinculados a un rol.
 *
 * UX:
 * - Búsqueda con debounce (200ms) y Ctrl/Cmd + F para enfocar.
 * - Filtro "Solo activos".
 * - Orden por columnas (Nombre, Correo, Estado) con iconos.
 * - Avatares con iniciales y estado en pill.
 * - Copiar correos de la lista visible (respeta filtros/orden).
 * - Body con scroll vertical (max-height 60vh) y header pegajoso.
 *
 * @param {boolean} show
 * @param {() => void} onHide
 * @param {Array<{fullName?:string,email?:string,isActive?:boolean}>} users
 * @param {string} roleName (opcional) - nombre del rol para cabecera
 */
const UsersByRoleModal = ({ show, onHide, users = [], roleName }) => {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);
  const [sort, setSort] = useState({ by: "fullName", dir: "asc" });
  const [copied, setCopied] = useState(false);
  const searchRef = useRef(null);

  // Debounce
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim().toLowerCase()), 200);
    return () => clearTimeout(id);
  }, [query]);

  // Atajo: Ctrl/Cmd + F -> enfocar buscador
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "f") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Filtrado
  const filtered = useMemo(() => {
    let data = users;
    if (onlyActive) data = data.filter((u) => !!u.isActive);
    if (debounced) {
      data = data.filter((u) => {
        const name = (u.fullName || "").toLowerCase();
        const email = (u.email || "").toLowerCase();
        return name.includes(debounced) || email.includes(debounced);
      });
    }
    return data;
  }, [users, debounced, onlyActive]);

  // Orden
  const sorted = useMemo(() => {
    const copy = [...filtered];
    const { by, dir } = sort;
    copy.sort((a, b) => {
      const av =
        by === "status"
          ? a.isActive
            ? 1
            : 0
          : (a[by] ?? "").toString().toLowerCase();
      const bv =
        by === "status"
          ? b.isActive
            ? 1
            : 0
          : (b[by] ?? "").toString().toLowerCase();
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filtered, sort]);

  const toggleSort = (by) => {
    setSort((prev) =>
      prev.by === by
        ? { by, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { by, dir: "asc" }
    );
  };

  const SortIcon = ({ by }) =>
    sort.by === by ? (
      sort.dir === "asc" ? (
        <ChevronUp size={16} />
      ) : (
        <ChevronDown size={16} />
      )
    ) : null;

  const handleCopyEmails = async () => {
    const emails = sorted
      .map((u) => u.email)
      .filter(Boolean)
      .join(", ");
    try {
      await navigator.clipboard.writeText(emails);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* noop: podrías disparar un toast */
    }
  };

  const total = users.length;
  const visibles = sorted.length;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      {/* Header estilizado */}
      <Modal.Header
        closeButton
        className="border-0"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          background:
            "linear-gradient(135deg, rgba(102,16,242,0.06), rgba(13,110,253,0.06))",
        }}
      >
        <div className="d-flex align-items-center w-100">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle me-3"
            style={{
              width: 42,
              height: 42,
              background: "rgba(13,110,253,0.1)",
            }}
          >
            <Users size={20} />
          </div>
          <div className="flex-grow-1">
            <Modal.Title className="mb-0">
              Usuarios con este rol{roleName ? `: ${roleName}` : ""}
            </Modal.Title>
            <div className="text-muted small">
              {total} total · {visibles} visibles
              {debounced ? ` · filtro: “${query}”` : ""}
              {onlyActive ? " · solo activos" : ""}
            </div>
          </div>

          {total > 0 && (
            <div className="d-flex gap-2">
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip>
                    {copied ? "¡Copiado!" : "Copiar correos visibles"}
                  </Tooltip>
                }
              >
                <Button
                  variant={copied ? "success" : "outline-secondary"}
                  size="sm"
                  onClick={handleCopyEmails}
                >
                  {copied ? (
                    <CheckCircle2 size={16} className="me-1" />
                  ) : (
                    <Copy size={16} className="me-1" />
                  )}
                  {copied ? "Copiado" : "Copiar"}
                </Button>
              </OverlayTrigger>
            </div>
          )}
        </div>
      </Modal.Header>

      {/* Body con scroll */}
      <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
        {total === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Filtros */}
            <div className="d-flex align-items-center justify-content-between mb-3">
              <InputGroup style={{ maxWidth: 420 }}>
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <FormControl
                  ref={searchRef}
                  placeholder="Buscar usuario o correo (Ctrl/Cmd+F)"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </InputGroup>

              <div className="d-flex align-items-center gap-3">
                <span className="text-muted d-none d-md-inline">
                  <Filter size={16} className="me-1" />
                  Filtros
                </span>
                <Form.Check
                  type="switch"
                  id="only-active-switch"
                  label="Solo activos"
                  checked={onlyActive}
                  onChange={(e) => setOnlyActive(e.target.checked)}
                />
              </div>
            </div>

            {/* Tabla */}
            {visibles === 0 ? (
              <div className="text-center text-muted py-4">
                <Search size={36} className="mb-2" />
                <div>No hay usuarios que coincidan con tu búsqueda.</div>
              </div>
            ) : (
              <Table hover responsive bordered className="mb-0">
                <thead className="align-middle">
                  <tr>
                    <th
                      role="button"
                      onClick={() => toggleSort("fullName")}
                      className="user-select-none text-center"
                    >
                      Nombre <SortIcon by="fullName" />
                    </th>
                    <th
                      role="button"
                      onClick={() => toggleSort("email")}
                      className="user-select-none text-center"
                    >
                      Correo <SortIcon by="email" />
                    </th>
                    <th
                      style={{ width: 140 }}
                      role="button"
                      onClick={() => toggleSort("status")}
                      className="user-select-none text-center"
                    >
                      Estado <SortIcon by="status" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((u, idx) => (
                    <tr key={`${u.email}-${idx}`}>
                      <td>
                        <div className="d-flex align-items-center">
                          <Avatar
                            name={u.fullName || u.email}
                            className="me-2"
                          />
                          <span className="fw-medium text-dark">
                            {u.fullName || "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <Mail size={14} className="me-1 text-muted" />
                        <span className="fw-medium text-dark">
                          {u.email || "-"}
                        </span>
                      </td>
                      <td className="text-center">
                        {u.isActive ? (
                          <Badge bg="success" className="px-2">
                            Activo
                          </Badge>
                        ) : (
                          <Badge bg="secondary" className="px-2">
                            Inactivo
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0">
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/** Avatar simple con iniciales (fallback visual amigable) */
const Avatar = ({ name = "", className = "" }) => {
  const initials = (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((x) => x[0]?.toUpperCase())
    .join("");
  const hue = Math.abs(hashCode(name)) % 360;
  return (
    <div
      className={`rounded-circle d-inline-flex align-items-center justify-content-center ${className}`}
      style={{
        width: 28,
        height: 28,
        fontSize: 12,
        background: `hsl(${hue} 70% 92%)`,
        color: `hsl(${hue} 50% 30%)`,
      }}
      aria-label={`Avatar de ${name}`}
      title={name}
    >
      {initials || "?"}
    </div>
  );
};

const hashCode = (str) =>
  (str || "")
    .split("")
    .reduce((acc, ch) => ((acc << 5) - acc + ch.charCodeAt(0)) | 0, 0);

const EmptyState = () => (
  <div className="text-center text-muted py-4">
    <Users size={40} className="mb-2" />
    <div className="mb-1">No hay usuarios asociados a este rol.</div>
    <small>
      Puedes asignar usuarios desde la vista de administración correspondiente.
    </small>
  </div>
);

export default UsersByRoleModal;
